import { useState, useEffect } from 'react';
import api from '../../../services/api/axiosConfig';
import API_ENDPOINTS from '../../../services/api/endpoints';

import PageHeading from '../../../components/PageHeading';
import { useNavigate } from 'react-router-dom';
// and included in your main CSS file for these styles to work.

const UserConversationsList = ({ currentUserId }) => {
    // State to store conversation data
    const [conversations, setConversations] = useState([]);
    // State to manage loading status
    const [loading, setLoading] = useState(true);
    // State to store any error messages
    const [error, setError] = useState(null);

    // The userId prop will be used for displaying to the user,
    const userId = currentUserId || 'Not Logged In';


    const navigate = useNavigate();

    useEffect(() => {
        const fetchConversations = async () => {
            try {
                setLoading(true);
                setError(null);

                // The backend is expected to derive the user ID from the attached bearer token.
                const response = await api.get(API_ENDPOINTS.PANEL.CUSTOMER.MESSAGE.CONVERSATION_LISTING);

                // *** ADJUSTMENT START ***
                // The new API response directly contains the 'data' array with conversations
                const data = response.data; // Axios wraps the actual response in a 'data' property

                if (data && data.data) { // Check if 'data' and 'data.data' exist
                    setConversations(data.data); // Set the 'data' array from the API response
                } else {
                    setError('No conversations data found in the API response.');
                }
                // *** ADJUSTMENT END ***
            } catch (err) {
                // Axios errors have a specific structure
                const errorMessage = err.message || 'An unknown error occurred.';
                const apiError = err.response && err.response.data && err.response.data.message
                                        ? err.response.data.message
                                        : errorMessage;
                setError('Failed to fetch conversations: ' + apiError);
            } finally {
                setLoading(false); // Ensure loading is set to false in all cases
            }
        };

        fetchConversations();
    }, [userId]); // Re-run effect if userId changes (though API call doesn't use it directly)

    // Helper function to format dates (not used in the current simplified UI but kept for completeness)
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid Date';

        const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return date.toLocaleDateString(undefined, options);
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow p-6 max-w-2xl mx-auto mt-8 flex flex-col items-center">
                <div className="w-10 h-10 border-4 border-t-4 border-gray-300 border-solid rounded-full animate-spin border-t-blue-500 mb-3"></div>
                <p className="text-gray-700 font-medium">Loading conversations...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 p-6 rounded-lg text-red-700 text-center mx-auto max-w-2xl mt-8 border border-red-300 shadow">
                <p className="font-extrabold text-xl mb-2">Oops! Something went wrong.</p>
                <p className="text-lg">{error}</p>
            </div>
        );
    }

    return (
        // Main container matching the image's layout (within the larger page structure)
        <div className="flex-1 p-6"> {/* Added flex-1 and p-6 to mimic the main content area */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                {/* Header section consistent with the image */}
                <div className="bg-purple-700 p-4">
                    <h1 className="font-semibold text-white text-lg ml-3">
                        <PageHeading value="Your Conversations" />
                    </h1>
                </div>

                {/* Conversations list */}
                <div className="p-4">
                    {conversations.length === 0 ? (
                        <div className="p-8 text-center text-gray-500 rounded border border-gray-200">
                            <p className="text-lg font-semibold mb-2">No conversations found.</p>
                            <p className="text-md text-gray-400">Looks like your inbox is empty. Start a new chat!</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {conversations.map((conv) => (
                                <div
                                    key={conv.conversation_id}
                                    onClick={() => navigate('/dashboard/messages/' + conv.conversation_id)}

                                    className="bg-white border border-gray-200 rounded-lg p-3 flex items-center space-x-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                                >
                                    {/* Avatar */}
                                    <div className="relative flex-shrink-0">
                                        <div className="w-14 h-14 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                                            {conv.other_participant_name ? conv.other_participant_name.charAt(0).toUpperCase() : '?'}
                                        </div>
                                        {/* Online status indicator (assuming always online for this demo) */}
                                        <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full ring-2 ring-white"></span>
                                    </div>

                                    {/* Conversation details */}
                                    <div className="flex-1 min-w-0" >
                                        <p className="text-lg font-medium text-gray-900 truncate">
                                            {conv.other_participant_name || 'Unknown User'}
                                        </p>
                                        <p className="text-sm text-gray-600 truncate">
                                            {conv.latest_message_body || 'No messages yet.'}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserConversationsList;